import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import { PlayerPeekProvider } from './context/PlayerPeekContext';
import { API_BASE_URL } from './api/axios';
import './i18n';

import Navbar from './components/Navbar';

const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const InviteNew = lazy(() => import('./pages/InviteNew'));
const Matches = lazy(() => import('./pages/Matches'));
const ActivityTypeMatches = lazy(() => import('./pages/ActivityTypeMatches'));
const AvailableMatches = lazy(() => import('./pages/AvailableMatches'));
const Messages = lazy(() => import('./pages/Messages'));
const MatchDetail = lazy(() => import('./pages/MatchDetail'));
const MatchResult = lazy(() => import('./pages/MatchResult'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Profile = lazy(() => import('./pages/Profile'));
const AdminPanel = lazy(() => import('./pages/admin/AdminPanel'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const Tournaments = lazy(() => import('./pages/Tournaments'));
const TournamentNew = lazy(() => import('./pages/TournamentNew'));
const TournamentDetail = lazy(() => import('./pages/TournamentDetail'));

function RouteFallback() {
  return <div className="min-h-screen" style={{ background: '#0D0D0D' }} aria-hidden />;
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function AppContent() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const settings = useSettings();
  const location = useLocation();

  /* Heavy bg only on match flows — not on / (landing) or auth pages */
  const isMatchPage = /^\/(matches(\/.*)?|match\/.*)$/.test(location.pathname);
  const bgFile =
    settings != null && isMatchPage && settings.active_bg_image
      ? `${API_BASE_URL}/uploads/${settings.active_bg_image}`
      : null;
  const isVideoBg = Boolean(bgFile && settings?.active_bg_type === 'video');
  const isImageBg = Boolean(bgFile && settings?.active_bg_type !== 'video');

  const [heavyBgReady, setHeavyBgReady] = useState(false);

  useEffect(() => {
    const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    if (!bgFile) {
      setHeavyBgReady(false);
      return;
    }

    setHeavyBgReady(false);
    let cancelled = false;

    const enable = () => {
      if (!cancelled) setHeavyBgReady(true);
    };

    const schedule = () => {
      if (cancelled) return;
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => enable(), { timeout: 2000 });
      } else {
        setTimeout(enable, 0);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      const onLoad = () => schedule();
      window.addEventListener('load', onLoad, { once: true });
      return () => {
        cancelled = true;
        window.removeEventListener('load', onLoad);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [bgFile]);

  if (settings?.maintenance_mode === 'true' && user?.role !== 'admin') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Maintenance message={settings.maintenance_message} />
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ background: '#0D0D0D' }}>

      {isVideoBg && heavyBgReady && (
        <video
          key={bgFile}
          autoPlay
          muted
          loop
          playsInline
          className="fixed inset-0 w-full h-full object-cover pointer-events-none"
          style={{ zIndex: 0, filter: 'blur(6px) brightness(0.8)', transform: 'scale(1.05)' }}
        >
          <source src={bgFile} />
        </video>
      )}

      {isImageBg && heavyBgReady && (
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${bgFile})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(6px) brightness(0.8)',
            transform: 'scale(1.05)',
            zIndex: 0,
          }}
        />
      )}

      {bgFile && heavyBgReady && (
        <div
          className="fixed inset-0 pointer-events-none"
          style={{ background: 'rgba(10,14,26,0.85)', zIndex: 1 }}
        />
      )}

      <div className="relative" style={{ zIndex: 2 }}>
        <Navbar />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/invite/new" element={<ProtectedRoute><InviteNew /></ProtectedRoute>} />
            <Route path="/matches/available" element={<ProtectedRoute><AvailableMatches /></ProtectedRoute>} />
            <Route path="/matches/list/:activityType" element={<ProtectedRoute><ActivityTypeMatches /></ProtectedRoute>} />
            <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
            <Route path="/match/:id" element={<ProtectedRoute><MatchDetail /></ProtectedRoute>} />
            <Route path="/match/:id/result" element={<ProtectedRoute><MatchResult /></ProtectedRoute>} />
            <Route path="/leaderboard" element={
              settings?.leaderboard_visible === 'false' && user?.role !== 'admin'
                ? <Navigate to="/dashboard" replace />
                : <Leaderboard />
            } />
            <Route path="/tournaments" element={<ProtectedRoute><Tournaments /></ProtectedRoute>} />
            <Route path="/tournaments/new" element={<ProtectedRoute><TournamentNew /></ProtectedRoute>} />
            <Route path="/tournaments/:id" element={<ProtectedRoute><TournamentDetail /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <PlayerPeekProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </PlayerPeekProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}
